import classNames from 'classnames'
import { AnimatePresence, motion, Variants } from 'framer-motion'
import React from 'react'
import { FieldError, UseFormRegister } from 'react-hook-form'

import { commonIcons } from '@apps-common/config/icons'
import { ISelectOption } from '@apps-common/types/form'

const variants: Variants = {
  initial: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.2
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  registration: Partial<UseFormRegister<any>>
  containerClassName?: string
  labelClassName?: string
  iconClassName?: string
  label: string
  showLabel?: boolean
  errors?: FieldError
  info?: string
  size?: 'sm' | 'lg'
  fixedLoading?: boolean
  loading?: boolean
  disabled?: boolean
  hideError?: boolean
  CustomIcon?: any
  options: ISelectOption[]
}

export const SelectInputSM = React.forwardRef<HTMLInputElement, React.PropsWithChildren<InputProps>>((props: InputProps, ref) => {
  const {
    className,
    containerClassName,
    labelClassName,
    registration,
    placeholder,
    autoComplete = 'off',
    id,
    showLabel = false,
    label,
    errors,
    info,
    fixedLoading = false,
    loading = false,
    disabled = false,
    options,
    hideError = false,
    CustomIcon,
    iconClassName
  } = props

  const Icon = commonIcons.chevronDownSharp

  const defaultOptions = []

  if (placeholder && placeholder?.length > 0) {
    defaultOptions.push(
      <option key={`${id}-select-option-placeholder`} value=''>
        {placeholder}
      </option>
    )
  }

  return (
    <AnimatePresence mode='wait'>
      {loading && (
        <motion.div
          key={`${id}-select-sm-skeleton`}
          initial='initial'
          animate='animate'
          exit='exit'
          variants={variants}
          className={classNames('mb-2', !fixedLoading && 'animate-pulse', containerClassName)}
        >
          <div className={classNames('block ml-2 mb-2 h-[16px] bg-skeleton max-w-[20%] rounded-lg', { 'sr-only': !showLabel })} />
          <div className='relative'>
            <div className={classNames('px-4 py-2 rounded-3xl bg-skeleton w-full h-[35px]', className)} />
          </div>
          {!hideError && <p className='pl-2 pt-2 h-[5px]' />}
        </motion.div>
      )}
      {!loading && (
        <motion.div
          key={`${id}-select-sm`}
          initial='initial'
          animate='animate'
          exit='exit'
          variants={variants}
          className={classNames('mb-2', containerClassName)}
        >
          <label
            className={classNames('block pl-2 pb-2 text-sm text-text-muted jost-medium', labelClassName, { 'sr-only': !showLabel })}
            htmlFor={id}
          >
            {label}
          </label>
          <div className='relative flex items-center'>
            <select
              id={id}
              className={classNames(
                className,
                'input !border-border text-text w-full text-sm h-[35px] rounded-[20px] !outline-none disable-input pr-[16px] bg-surface',
                { 'input-error': errors },
                disabled && 'text-text/50 !bg-text/5'
              )}
              disabled={disabled}
              {...registration}
            >
              {[
                ...defaultOptions,
                ...options.map((o, i) => (
                  <option key={`${id}-select-option-${i}`} value={o.value?.toString()}>
                    {o.label}
                  </option>
                ))
              ]}
            </select>
            {CustomIcon && (
              <CustomIcon
                className={classNames('absolute right-[10px] text-text pointer-events-none', disabled && 'opacity-40', iconClassName)}
              />
            )}
            {!CustomIcon && (
              <Icon
                className={classNames('absolute right-[10px] text-text pointer-events-none', disabled && 'opacity-40', iconClassName)}
              />
            )}
          </div>
          {!hideError && <p className='pl-2 pt-2 text-error text-xs'>{errors?.message}</p>}
          {!errors?.message && info && <p className='pl-2 pt-0 text-[0.75rem] text-text-muted/80 pb-2'>{info}</p>}
        </motion.div>
      )}
    </AnimatePresence>
  )
})

SelectInputSM.displayName = 'Input'
